import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Form, Row, Col } from 'react-bootstrap';
import '../styles/ContactForm.css';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';
import getRecaptchaToken from '../helpers/getRecaptchaToken';
import AppContext from './context/AppContext';

const invalidNameRegExp = /[^A-Za-z\-' ]/g;

const ContactForm = ({ }) => {
    const { appContext } = useContext(AppContext);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        if (!name || !email || !message) {
            return;
        }
        try {
            setIsSubmitting(true);
            const response = await fetch(
                new URL('/api/contactForm', process.env.APP_URL),
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify({
                        name,
                        email,
                        company,
                        phone,
                        message,
                        token: await getRecaptchaToken(appContext.config.recaptchaSiteKey),
                    }),
                }
            );
            const data = await response.json();
            setIsSubmitting(false);
            if (!response.ok || data.error) {
                throw new Error(data.error ?? 'Something went wrong.');
            }
            setSuccess(true);
            setName('');
            setEmail('');
            setCompany('');
            setPhone('');
            setMessage('');
        } catch (e) {
            setErrorMessage(e.message);
            console.error(e);
        }
    };

    useEffect(() => {
        const successTimer = null;
        const SUCCESS_TIMEOUT_MS = 3000;
        const successTimerFunc = () => setSuccess(false);
        if (success === true) {
            setTimeout(successTimerFunc, SUCCESS_TIMEOUT_MS);
        }
        return () => {
            clearTimeout(successTimer);
        };
    }, [success]);

    const handleCompanyChange = useCallback((e) => {
        setCompany(e.target.value);
    }, []);

    return (
        <>
            <div
                className={'contact-form--intro-text'}>
                <hr
                    className={'contact-form--divider'} />
                <p>If you have a question or comment for us, please use the form below to submit it. A Sourcefy representative will respond to your inquiry as soon as possible.</p>
                <p>Our highest priority is your satisfaction.</p>
            </div>

            <Row
                className={'contact-form--page-container'}>
                <Col
                    md={7}
                    className={'contact-form--form-container'}>
                    <Form
                        onSubmit={handleSubmit}>
                        <Form.Group
                            className={'mb-3'}
                            controlId={'contactForm.name'}>
                            <Form.Label>Full Name</Form.Label>
                            <Form.Control
                                name={'formname'}
                                type={'text'}
                                placeholder={'Your full name'}
                                value={name}
                                disabled={isSubmitting}
                                onChange={(e) => setName(e.target.value)}
                                isValid={name.length > 0 && !name.match(invalidNameRegExp)}
                                isInvalid={name.match(invalidNameRegExp) !== null || name.length > 255}
                                required
                            />
                        </Form.Group>

                        <Form.Group
                            className={'mb-3'}
                            controlId={'contactForm.email'}>
                            <Form.Label>* Email Address</Form.Label>
                            <Form.Control
                                name={'formemail'}
                                type={'text'}
                                placeholder={'Email address'}
                                value={email}
                                disabled={isSubmitting}
                                onChange={(e) => setEmail(e.target.value)}
                                isValid={isEmail(email)}
                                required
                            />
                        </Form.Group>

                        <Form.Group
                            className={'mb-3'}
                            controlId={'contactForm.company'}>
                            <Form.Label>Company Name</Form.Label>
                            <Form.Control
                                name={'formcompany'}
                                type={'text'}
                                placeholder={'Your company'}
                                value={company}
                                disabled={isSubmitting}
                                onChange={handleCompanyChange}
                            />
                        </Form.Group>

                        <Form.Group
                            className={'mb-3'}
                            controlId={'contactForm.phone'}>
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control
                                name={'formphone'}
                                type={'text'}
                                placeholder={'Phone number'}
                                value={phone}
                                disabled={isSubmitting}
                                onChange={(e) => setPhone(e.target.value)}
                                isValid={phone ? isMobilePhone(phone, 'any') : false}
                            />
                        </Form.Group>

                        <Form.Group
                            className={'mb-3'}
                            controlId={'contactForm.message'}>
                            <Form.Label>* Details</Form.Label>
                            <Form.Control
                                as={'textarea'}
                                rows={5}
                                placeholder={'Your message'}
                                value={message}
                                disabled={isSubmitting}
                                onChange={(e) => setMessage(e.target.value)}
                                required
                                isValid={message.length >= 3 && message.length <= 1500}
                            />
                        </Form.Group>

                        <div
                            className={'contact-form--recaptcha-container mb-3'}>
                            <div
                                className={'g-recaptcha'}
                                data-sitekey={appContext.config.recaptchaSiteKey}></div>
                        </div>

                        <Button
                            className={'custom-btn contact-form--send-btn'}
                            type={'submit'}
                            onClick={handleSubmit}
                            disabled={(!name || !email || !message || isSubmitting)}>
                            SEND FORM
                        </Button>

                        {errorMessage !== '' && (
                            <p
                                style={{
                                    color: 'red',
                                    marginTop: '15px'
                                }}>{errorMessage}</p>
                        )}

                        {success === true && (
                            <p
                                style={{
                                    color: 'green',
                                    marginTop: '15px'
                                }}>Your message has been received. Thank you.</p>
                        )}
                    </Form>
                </Col>

                <Col
                    md={5}
                    className={'contact-form--info-container'}>
                    <div
                        className={'contact-form--mailing-address'}>
                        <h4>MAILING ADDRESS</h4>
                        <p>
                            #108-209 GLEN COVE RD,<br />
                            CARLE PLACE, NY, US, 11514
                        </p>
                        <p>
                            Email: support@sourcefy.com<br />
                            Toll-free: 1855-585-4430
                        </p>
                        <hr />
                        <p>
                            Office hours:<br />
                            6am to 4pm Pacific Standard Time<br />
                            Online orders open 24/7
                        </p>
                    </div>
                </Col>
            </Row>
        </>
    );
};

export default ContactForm;